<template>
  <div
    id="app"
    class="app-shell"
    :class="shellClass"
    :style="theme.cssVars"
  >
    <!-- Navigation -->
    <nav v-show="!hideAppNav" class="top-app-bar">
      <div class="bar-wrap">
        <div ref="navBarEl" class="bar-inner">
          <div class="nav-start">
            <router-link
              to="/"
              class="brand"
              :aria-label="t('nav.brand')"
            >
              <img
                src="/icons/icon.svg"
                alt=""
                class="brand-mark"
                width="36"
                height="36"
              >
            </router-link>
            <div
              v-show="!menusCompact"
              class="nav-links"
              :class="{ 'nav-measure': navCrowded }"
              :inert="navCrowded"
            >
              <router-link
                to="/"
                class="nav-link"
                :class="{ 'active': $route.name === 'home' }"
              >
                {{ t('nav.home') }}
              </router-link>
              <router-link
                to="/packs"
                class="nav-link"
                :class="{ 'active': $route.name === 'packs' }"
              >
                {{ t('nav.packs') }}
              </router-link>
              <router-link
                to="/missions"
                class="nav-link nav-missions"
                :class="{ 'active': $route.name === 'daily-missions' }"
              >
                {{ t('nav.missions') }}
                <span
                  v-if="missions.claimableCount > 0"
                  class="nav-notice"
                  :aria-label="t('missions.pendingBadge', { n: missions.claimableCount })"
                >{{ missions.claimableCount }}</span>
              </router-link>
              <router-link
                to="/deck-builder"
                class="nav-link"
                :class="{
                  'active': $route.path.startsWith('/deck-builder'),
                }"
              >
                {{ t('nav.deckBuilder') }}
              </router-link>
              <router-link
                to="/collection"
                class="nav-link"
                :class="{ 'active': $route.name === 'collection' }"
              >
                {{ t('nav.collection') }}
              </router-link>
              <router-link
                to="/changelog"
                class="nav-link nav-news"
                :class="{ active: $route.name === 'changelog' }"
                :aria-label="newsLinkLabel"
              >
                {{ t('nav.news') }}
                <span v-if="hasPendingNews" class="nav-pending" aria-hidden="true" />
              </router-link>
              <router-link
                to="/banlist"
                class="nav-link"
                :class="{ active: $route.name === 'banlist' }"
              >
                {{ t('nav.banlist') }}
              </router-link>
              <router-link
                to="/meta"
                class="nav-link"
                :class="{ active: $route.name === 'meta' }"
              >
                {{ t('nav.popular') }}
              </router-link>
              <router-link
                to="/tournaments"
                class="nav-link"
                :class="{ active: $route.name === 'tournaments' || $route.name === 'tournament-detail' }"
              >
                {{ t('nav.tournaments') }}
              </router-link>
              <router-link
                to="/faq"
                class="nav-link"
                :class="{ active: $route.name === 'faq' }"
              >
                {{ t('nav.faq') }}
              </router-link>
              <button
                type="button"
                class="nav-link nav-duel"
                :class="{ active: duelPickOpen || $route.name === 'online-duel' }"
                aria-haspopup="dialog"
                :aria-expanded="duelPickOpen"
                @click="openDuelPick"
              >
                {{ t('nav.duel') }}
              </button>
            </div>
          </div>

          <div class="nav-end">
            <div
              v-show="!menusCompact"
              class="nav-extras"
              :class="{ 'nav-measure': navCrowded }"
              :inert="navCrowded"
            >
            <router-link
              to="/missions"
              class="nav-chip coin-chip"
              data-coin-chip
              :title="t('coins.balance')"
            >
              <CoinToken size="sm" />
              <span class="coin-amt">{{ displayCoins }}</span>
            </router-link>
            <span
              class="nav-chip level-chip"
              :title="t('level.title')"
            >
              {{ t('level.chip', { n: progress.level }) }}
            </span>

            <button
              type="button"
              class="icon-btn"
              :title="
                theme.colorScheme === 'dark'
                  ? t('settings.light')
                  : t('settings.dark')
              "
              :aria-label="
                theme.colorScheme === 'dark'
                  ? t('settings.light')
                  : t('settings.dark')
              "
              @click="theme.toggleScheme()"
            >
              <svg
                v-if="theme.colorScheme === 'dark'"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364-.707-.707M6.343 6.343l-.707-.707m12.728 0-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <svg
                v-else
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>
            </button>

            <LanguageSelect />

            <div
              ref="accountMenuEl"
              class="account-menu"
            >
              <button
                type="button"
                class="nav-link nav-profile"
                :class="{ active: onAccount }"
                :aria-expanded="accountMenuOpen"
                :aria-haspopup="true"
                :aria-label="accountMenuLabel"
                @click="accountMenuOpen = !accountMenuOpen"
              >
                <AvatarView
                  :config="player.avatar"
                  crop="bust"
                  circle
                  :size="40"
                />
                {{ player.username }}
                <span
                  v-if="friends.incomingCount > 0"
                  class="nav-notice nav-notice-menu"
                  aria-hidden="true"
                >{{ friends.incomingCount > 9 ? '9+' : friends.incomingCount }}</span>
                <svg
                  class="caret"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
              <div
                v-if="accountMenuOpen"
                class="account-pop"
                role="menu"
              >
                <router-link
                  to="/profile"
                  role="menuitem"
                  class="account-item"
                  @click="accountMenuOpen = false"
                >
                  {{ t('nav.profile') }}
                </router-link>
                <router-link
                  to="/friends"
                  role="menuitem"
                  class="account-item"
                  @click="accountMenuOpen = false"
                >
                  {{ t('nav.friends') }}
                  <span
                    v-if="friends.incomingCount > 0"
                    class="nav-notice nav-notice-inline"
                    :aria-label="t('friends.incoming')"
                  >{{ friends.incomingCount > 99 ? '99+' : friends.incomingCount }}</span>
                </router-link>
                <router-link
                  to="/settings"
                  role="menuitem"
                  class="account-item"
                  @click="accountMenuOpen = false"
                >
                  {{ t('nav.settings') }}
                </router-link>
                <router-link
                  v-if="auth.user?.isAdmin"
                  :to="{ name: 'ops' }"
                  role="menuitem"
                  class="account-item"
                  @click="accountMenuOpen = false"
                >
                  {{ t('nav.admin') }}
                </router-link>
                <div class="account-sep" role="separator" />
                <button
                  type="button"
                  class="account-item"
                  role="menuitem"
                  @click="onLogout"
                >
                  {{ t('auth.logout') }}
                </button>
              </div>
            </div>
            </div>

            <div class="nav-persistent">
              <DesktopFullscreenButton tone="icon" />
              <router-link
                to="/mailbox"
                class="icon-btn nav-mail"
                :aria-label="mailLinkLabel"
              >
                <svg
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span
                  v-if="mailBadge > 0"
                  class="nav-notice"
                  aria-hidden="true"
                >{{ mailBadgeText }}</span>
              </router-link>
            </div>

            <router-link
              v-show="navCompact"
              to="/missions"
              class="nav-chip coin-chip"
              data-coin-chip
              :title="t('coins.balance')"
            >
              <CoinToken size="sm" />
              <span class="coin-amt">{{ displayCoins }}</span>
            </router-link>
            <span
              v-show="navCompact"
              class="nav-chip level-chip"
              :title="t('level.title')"
            >
              {{ t('level.chip', { n: progress.level }) }}
            </span>
            <button
              v-show="navCompact"
              type="button"
              class="icon-btn"
              :aria-expanded="mobileMenuOpen"
              :aria-label="menuAria"
              @click="mobileMenuOpen = !mobileMenuOpen"
            >
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round"
                  stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span
                v-if="missions.claimableCount > 0"
                class="nav-notice nav-notice-menu"
                aria-hidden="true"
              >{{ missions.claimableCount }}</span>
              <span
                v-if="friends.incomingCount > 0"
                class="nav-notice nav-notice-menu"
                :class="{ 'nav-notice-alt': missions.claimableCount > 0 }"
                aria-hidden="true"
              >{{ friends.incomingCount > 9 ? '9+' : friends.incomingCount }}</span>
              <span
                v-if="hasPendingNews"
                class="nav-pending nav-pending-menu"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>
    </nav>

    <div
      v-if="navCompact && mobileMenuOpen"
      class="drawer-scrim"
      @click="mobileMenuOpen = false"
    >
      <div class="mobile-drawer" @click.stop>
        <CloseXButton
          class="drawer-close"
          :label="t('mobile.closeMenu')"
          @click="mobileMenuOpen = false"
        />
        <button
          type="button"
          class="mobile-nav-link mobile-nav-duel"
          aria-haspopup="dialog"
          :aria-expanded="duelPickOpen"
          @click="openDuelPick"
        >
          {{ t('nav.duel') }}
        </button>
        <router-link to="/" class="mobile-nav-link">{{ t('nav.home') }}</router-link>
        <router-link to="/packs" class="mobile-nav-link">{{ t('nav.packs') }}</router-link>
        <router-link to="/missions" class="mobile-nav-link mobile-nav-missions">
          {{ t('nav.missions') }}
          <span
            v-if="missions.claimableCount > 0"
            class="nav-notice nav-notice-inline"
            :aria-label="t('missions.pendingBadge', { n: missions.claimableCount })"
          >{{ missions.claimableCount }}</span>
        </router-link>
        <router-link to="/rank" class="mobile-nav-link">
          {{ t('nav.rank') }}
        </router-link>
        <router-link to="/deck-builder" class="mobile-nav-link">
          {{ t('nav.deckBuilder') }}
        </router-link>
        <router-link to="/collection" class="mobile-nav-link">
          {{ t('nav.collection') }}
        </router-link>
        <router-link
          to="/changelog"
          class="mobile-nav-link mobile-nav-news"
          :aria-label="newsLinkLabel"
        >
          {{ t('nav.news') }}
          <span v-if="hasPendingNews" class="nav-pending" aria-hidden="true" />
        </router-link>
        <router-link
          to="/banlist"
          class="mobile-nav-link"
          :class="{ active: route.name === 'banlist' }"
        >
          {{ t('nav.banlist') }}
        </router-link>
        <router-link
          to="/meta"
          class="mobile-nav-link"
          :class="{ active: route.name === 'meta' }"
        >
          {{ t('nav.popular') }}
        </router-link>
        <router-link
          to="/tournaments"
          class="mobile-nav-link"
          :class="{ active: route.name === 'tournaments' || route.name === 'tournament-detail' }"
        >
          {{ t('nav.tournaments') }}
        </router-link>
        <router-link
          to="/faq"
          class="mobile-nav-link"
          :class="{ active: route.name === 'faq' }"
        >
          {{ t('nav.faq') }}
        </router-link>
        <router-link
          to="/friends"
          class="mobile-nav-link"
          :class="{ active: route.name === 'friends' }"
        >
          {{ t('nav.friends') }}
          <span
            v-if="friends.incomingCount > 0"
            class="nav-notice nav-notice-inline"
          >{{ friends.incomingCount > 99 ? '99+' : friends.incomingCount }}</span>
        </router-link>
        <router-link to="/mailbox" class="mobile-nav-link mobile-nav-mail">
          {{ t('nav.mailbox') }}
          <span
            v-if="mailBadge > 0"
            class="nav-notice nav-notice-inline"
            :aria-label="t('mailbox.pendingBadge', { n: mailBadge })"
          >{{ mailBadgeText }}</span>
        </router-link>
        <router-link
          to="/settings"
          class="mobile-nav-link"
          :class="{ active: route.name === 'settings' }"
        >
          {{ t('nav.settings') }}
        </router-link>
        <div class="drawer-stats">
          <router-link
            to="/missions"
            class="nav-chip coin-chip"
            data-coin-chip
            :title="t('coins.balance')"
          >
            <CoinToken size="sm" />
            <span class="coin-amt">{{ displayCoins }}</span>
          </router-link>
          <span class="nav-chip level-chip" :title="t('level.title')">
            {{ t('level.chip', { n: progress.level }) }}
          </span>
        </div>
        <router-link
          to="/profile"
          class="mobile-nav-link mobile-profile"
          :class="{ active: route.name === 'profile' }"
        >
          <AvatarView
            :config="player.avatar"
            crop="bust"
            circle
            :size="menusShort ? 48 : 80"
          />
          <span class="mobile-profile-name">{{ player.username }}</span>
        </router-link>
        <router-link
          v-if="auth.user?.isAdmin"
          :to="{ name: 'ops' }"
          class="mobile-nav-link"
        >
          {{ t('nav.admin') }}
        </router-link>
        <div class="drawer-extras">
          <div class="theme-toggle">
            <span class="theme-toggle-copy">{{ t('settings.theme') }}</span>
            <button
              type="button"
              class="theme-switch"
              role="switch"
              :aria-checked="theme.colorScheme === 'light'"
              :aria-label="t('settings.theme')"
              @click="theme.toggleScheme()"
            >
              <span
                class="theme-switch-knob"
                :class="{ light: theme.colorScheme === 'light' }"
                aria-hidden="true"
              >
                <svg
                  v-if="theme.colorScheme === 'dark'"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                  />
                </svg>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364-.707-.707M6.343 6.343l-.707-.707m12.728 0-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </span>
            </button>
            <span class="theme-toggle-mode">
              {{
                theme.colorScheme === 'light'
                  ? t('settings.light')
                  : t('settings.dark')
              }}
            </span>
          </div>
          <LanguageSelect class="drawer-lang" />
          <PwaInstallButton tone="drawer" />
        </div>
        <button
          type="button"
          class="mobile-nav-link drawer-btn drawer-logout"
          @click="onLogout"
        >
          {{ t('auth.logout') }}
        </button>
        <div class="drawer-support">
          <SupportButton />
        </div>
      </div>
    </div>

    <main class="app-main" :class="{ 'has-bug-report': showBugReport }">
      <router-view />
    </main>

    <BootIntro v-if="showIntro" @done="onIntroDone" />
    <WelcomeOverlay
      v-if="auth.token && collection.welcomePending"
      @continue="onWelcomeContinue"
    />
    <BanlistRewardDialog
      v-if="showBanlistReward"
      @later="banlistReward.snooze()"
    />
    <NeedDeckOverlay
      v-if="auth.token && needDeck && !collection.welcomePending && !anyPlayableDeck"
      @close="closeNeedDeck"
    />
    <MailClaimDialog
      v-if="showMailDialog && focusedMail"
      :mail="focusedMail"
    />
    <ChangelogDialog
      v-if="showChangelogDialog"
      :releases="dialogSnapshot"
      @close="changelogDialogOpen = false"
      @snooze="onChangelogSnooze"
    />
    <EventQueueDialog
      v-if="showEventQueueDialog"
      @close="onEventQueueClose"
      @snooze="onEventQueueSnooze"
      @play="onEventQueuePlay"
    />
    <LastCallDialog
      v-if="showLastCallDialog"
      @close="onLastCallClose"
      @snooze="onLastCallSnooze"
    />
    <button
      v-if="friendToast"
      type="button"
      class="friend-toast"
      aria-live="polite"
      @click="onFriendToast"
    >{{ friendToast }}</button>
    <FriendInviteDialog
      v-if="chatUi.invite"
      :username="chatUi.invite.username"
      :code="chatUi.invite.code"
      @accept="onAcceptInvite"
      @decline="chatUi.declineInvite()"
    />
    <PushAskDialog
      v-if="showPushAsk"
      @enable="onPushAskEnable"
      @later="onPushAskLater"
    />
    <TournamentCallDialog
      v-if="tournamentCall"
      :opponent="tournamentCall.opponent"
      @play="onTournamentPlay"
      @later="tournamentCall = null"
    />
    <TournamentOpenDialog
      v-if="tournamentOpen"
      :starts-at="tournamentOpen.startsAt"
      @join="onTournamentOpenJoin"
      @later="tournamentOpen = null"
    />
    <TournamentCountdownToast :hold="showIntro" />
    <TournamentDeckDialog v-if="tournament.pickerOpen && tournament.current" />
    <GlobalChatPanel
      v-if="showChat"
      :messages="chatMessages"
      :online-count="onlineCount"
      :online-users="onlineUsers"
      :compact="chatCompact"
      @send="sendChat"
    />
    <BugReportButton v-if="showBugReport" />
    <RotateDeviceOverlay v-if="showRotateOverlay" />
    <MissionClaimToast />
    <InterruptRewardToast />
    <BugRewardToast />
    <StaffGrantToast />
    <SeasonRewardToast />
    <FoilDebugHud v-if="isDev" />
    <DuelModeSheet
      :visible="duelPickOpen"
      @cancel="duelPickOpen = false"
      @ai="startVsAi"
      @ranked="startRanked"
      @casual="startCasual"
      @event="startEvent"
    />
    <HttpBusySpinner v-if="!showIntro && !isPlayDuel" />
    <MaintenanceScreen v-if="showMaintenance" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { TOURNAMENT_READY_MS, type GlobalChatMessagePayload, type TournamentCallPayload } from '@shared/index'
import { useCollectionStore } from './stores/collection'
import { useI18n } from './i18n/t'
import { useThemeStore } from './stores/theme'
import { usePlayerStore } from './stores/player'
import { useAuthStore } from './stores/auth'
import { useWalletStore } from './stores/wallet'
import { useProgressStore } from './stores/progress'
import { useBanlistRewardStore } from './stores/banlistReward'
import { useMissionsStore } from './stores/missions'
import { useChatStore } from './stores/chat'
import { useChatMuteStore } from './stores/chatMutes'
import { useFriendsStore } from './stores/friends'
import { useRankedLobbyStore } from './stores/rankedLobby'
import { useTournamentStore } from './stores/tournament'
import { openTournamentMatch } from './tournament/go'
import {
  armTournamentPush,
  askTournamentPush,
  dismissPushAsk,
  pushAskDismissed,
  tournamentNoticesAllowed,
} from './push/arm'
import { tournamentPushOn } from './push/device'
import { useSiteStore } from './stores/site'
import MaintenanceScreen from './components/MaintenanceScreen.vue'
import { getClientId } from './game/net/clientId'
import { GlobalChatClient } from './game/net/GlobalChatClient'
import WelcomeOverlay from './components/WelcomeOverlay.vue'
import BanlistRewardDialog from './components/BanlistRewardDialog.vue'
import BootIntro from './components/BootIntro.vue'
import NeedDeckOverlay from './components/NeedDeckOverlay.vue'
import GlobalChatPanel from './components/GlobalChatPanel.vue'
import FriendInviteDialog from './components/FriendInviteDialog.vue'
import PushAskDialog from './components/PushAskDialog.vue'
import TournamentCallDialog from './components/TournamentCallDialog.vue'
import TournamentOpenDialog from './components/TournamentOpenDialog.vue'
import TournamentCountdownToast from './components/TournamentCountdownToast.vue'
import TournamentDeckDialog from './components/TournamentDeckDialog.vue'
import RotateDeviceOverlay from './components/RotateDeviceOverlay.vue'
import MissionClaimToast from './components/MissionClaimToast.vue'
import InterruptRewardToast from './components/InterruptRewardToast.vue'
import BugRewardToast from './components/BugRewardToast.vue'
import StaffGrantToast from './components/StaffGrantToast.vue'
import { applyStaffReward } from './account/staffGrantNotice'
import SeasonRewardToast from './components/SeasonRewardToast.vue'
import FoilDebugHud from './components/FoilDebugHud.vue'
import AvatarView from './components/AvatarView.vue'
import CloseXButton from './components/CloseXButton.vue'
import CoinToken from './components/CoinToken.vue'
import PwaInstallButton from './components/PwaInstallButton.vue'
import SupportButton from './components/SupportButton.vue'
import DesktopFullscreenButton from './components/DesktopFullscreenButton.vue'
import LanguageSelect from './components/LanguageSelect.vue'
import DuelModeSheet from './components/DuelModeSheet.vue'
import HttpBusySpinner from './components/HttpBusySpinner.vue'
import ChangelogDialog from './components/ChangelogDialog.vue'
import MailClaimDialog from './components/MailClaimDialog.vue'
import { mailFocusId, mailItems, mailUnread, receivePlayerMail } from './account/playerMail'
import LastCallDialog from './components/LastCallDialog.vue'
import EventQueueDialog from './components/EventQueueDialog.vue'
import BugReportButton from './components/BugReportButton.vue'
import { useChangelogNotice } from './composables/useChangelogNotice'
import { useLastCallNotice } from './composables/useLastCallNotice'
import { useEventQueueNotice } from './composables/useEventQueueNotice'
import type { ChangelogRelease } from './i18n/changelog'
import { DuelSfx } from './game/audio/DuelSfx'
import { useCompactLayout } from './composables/useCompactLayout'
import { MENUS_NARROW_MQ, MENUS_SHORT_MQ, mqMatches } from './ui/compactLayout'
import { usePwaInstall } from './composables/usePwaInstall'
import { useDesktopFullscreen } from './composables/useDesktopFullscreen'
import {
  armDuelLandscape,
  releaseDuelOrientation,
} from './composables/useDuelOrientation'
import { hydrateAccount, resetAccountLocal, startAccountSync } from './account/sync'
import { accountRename } from './account/liveRename'
import { applyChatRename, chatNamesMatch } from '@shared/index'
import { hasPlayableDeck } from './game/localDeck'
import { Bgm, bgmSceneForRoute } from './game/audio/Bgm'
import { installUiSfx } from './game/audio/UiSfx'
import { warmCatalogCardArt } from './utils/cardImageCache'

const PLAY_DUEL_ROUTES = new Set(['online-duel', 'duel'])
const AUTH_ROUTES = new Set(['login', 'register', 'forgot-password', 'reset-password'])
const isDev = import.meta.env.DEV

const { t, locale } = useI18n()
const { duelCompact, needsRotate, menusCompact, menusShort } = useCompactLayout()
/** First paint on a laptop: the link row is wider than the window. Measure corrects it. */
function navStartsCrowded(): boolean {
  if (typeof window === 'undefined') return false
  if (mqMatches(MENUS_NARROW_MQ) || mqMatches(MENUS_SHORT_MQ)) return false
  return window.matchMedia('(max-width: 1600px)').matches
}
const navCrowded = ref(navStartsCrowded())
const navCompact = computed(() => menusCompact.value || navCrowded.value)
const navBarEl = ref<HTMLElement | null>(null)
let navFitObserver: ResizeObserver | null = null
usePwaInstall()
const { active: desktopFullscreen } = useDesktopFullscreen()
const mobileMenuOpen = ref(false)
const duelPickOpen = ref(false)
const accountMenuOpen = ref(false)
const accountMenuEl = ref<HTMLElement | null>(null)
const INTRO_KEY = 'dc.bootIntro.v1'
const introForced = () =>
  new URLSearchParams(window.location.search).get('intro') === '1'
const showIntro = ref(
  introForced() || sessionStorage.getItem(INTRO_KEY) !== '1',
)
const needDeck = ref(false)
const anyPlayableDeck = computed(() => hasPlayableDeck())
const route = useRoute()
const router = useRouter()
const collection = useCollectionStore()
const theme = useThemeStore()
const player = usePlayerStore()
const auth = useAuthStore()
const wallet = useWalletStore()
const progress = useProgressStore()
const missions = useMissionsStore()
const changelogNotice = useChangelogNotice()
const lastCallNotice = useLastCallNotice()
const eventQueueNotice = useEventQueueNotice()
const hasPendingNews = changelogNotice.pending
const newsLinkLabel = computed(() =>
  hasPendingNews.value
    ? `${t('nav.news')}. ${t('changelog.pending')}`
    : undefined,
)
const mailBadge = computed(
  () => mailUnread.value + changelogNotice.unseen.value.length,
)
const mailBadgeText = computed(() =>
  mailBadge.value > 99 ? '99+' : String(mailBadge.value),
)
const mailLinkLabel = computed(() =>
  mailBadge.value > 0
    ? `${t('nav.mailbox')}. ${t('mailbox.pendingBadge', { n: mailBadge.value })}`
    : t('nav.mailbox'),
)
const focusedMail = computed(() => {
  const id = mailFocusId.value
  if (!id) return null
  return mailItems.value.find((row) => row.id === id) ?? null
})
const banlistReward = useBanlistRewardStore()
const showMailDialog = computed(
  () =>
    Boolean(focusedMail.value) &&
    Boolean(auth.token) &&
    !showIntro.value &&
    !collection.welcomePending &&
    !needDeck.value &&
    !isAuthScreen.value &&
    !isPlayDuel.value &&
    route.name !== 'ops',
)
const changelogDialogOpen = ref(false)
const lastCallDialogOpen = ref(false)
const lastCallHeld = ref(false)
const eventQueueDialogOpen = ref(false)
const eventQueueHeld = ref(false)
const dialogSnapshot = ref<ChangelogRelease[]>([])
// Paid when the mission finishes. Hold that prize out of the chip until Collect.
const displayCoins = computed(() =>
  Math.max(0, wallet.coins - missions.heldCoins),
)
const menuAria = computed(() => {
  const parts = [mobileMenuOpen.value ? t('mobile.closeMenu') : t('mobile.menu')]
  if (missions.claimableCount > 0) {
    parts.push(t('missions.pendingBadge', { n: missions.claimableCount }))
  }
  if (friends.incomingCount > 0) {
    parts.push(t('friends.pendingBadge', { n: friends.incomingCount }))
  }
  if (hasPendingNews.value) parts.push(t('changelog.pending'))
  if (mailBadge.value > 0) parts.push(t('mailbox.pendingBadge', { n: mailBadge.value }))
  return parts.join('. ')
})
const accountMenuLabel = computed(() => {
  if (friends.incomingCount < 1) return t('nav.menu')
  return `${t('nav.menu')}. ${t('friends.pendingBadge', { n: friends.incomingCount })}`
})
const chatUi = useChatStore()
const friends = useFriendsStore()
const friendToast = ref('')
let friendToastTimer = 0
let friendToastGo: (() => void) | null = null

function showFriendToast(text: string, opts?: { go?: () => void; ms?: number }): void {
  friendToast.value = text
  friendToastGo = opts?.go ?? null
  window.clearTimeout(friendToastTimer)
  friendToastTimer = window.setTimeout(() => {
    friendToast.value = ''
    friendToastGo = null
  }, opts?.ms ?? 4000)
}

function onFriendToast(): void {
  const go = friendToastGo
  friendToast.value = ''
  friendToastGo = null
  window.clearTimeout(friendToastTimer)
  go?.()
}

const seenIncoming = new Set<string>()
let incomingBaselined = false

function notifyFriendRequest(body: string): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  if (document.visibilityState === 'visible') return
  try {
    const note = new Notification(t('nav.friends'), { body })
    note.onclick = () => {
      window.focus()
      note.close()
      if (route.name !== 'friends') void router.push({ name: 'friends' })
    }
  } catch {
    /* the toast is there when the tab is visible again */
  }
}

function openFriends(): void {
  if (route.name !== 'friends') void router.push({ name: 'friends' })
}

function friendRequestText(names: readonly string[]): string {
  if (names.length === 1) return t('friends.requestIncoming', { name: names[0] })
  return t('friends.pendingBadge', { n: names.length })
}

const OUTGOING_NOTE_KEY = 'dc.friendOutgoing'

function outgoingNoteKey(): string | null {
  const id = auth.user?.id
  return id ? `${OUTGOING_NOTE_KEY}.${id}` : null
}

function readOutgoingNote(): Set<string> {
  const key = outgoingNoteKey()
  if (!key) return new Set()
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return new Set()
    return new Set(
      parsed
        .filter((name): name is string => typeof name === 'string')
        .map((name) => name.toLowerCase()),
    )
  } catch {
    return new Set()
  }
}

function writeOutgoingNote(names: readonly string[]): void {
  const key = outgoingNoteKey()
  if (!key) return
  try {
    localStorage.setItem(key, JSON.stringify(names))
  } catch {
    /* private mode */
  }
}

function friendAcceptedText(names: readonly string[]): string {
  if (names.length === 1) return t('friends.accepted', { name: names[0] })
  return t('friends.acceptedMany', { n: names.length })
}

watch(
  () => friends.friends.map((row) => `${row.username}:${row.unread}`).join('|'),
  () => {
    if (!friends.ready || friends.loadError) return
    chatUi.seedDmUnread(friends.friends)
    chatUi.pruneDm(friends.friends.map((row) => row.username))
  },
)

watch(
  () => friends.ready,
  (ready) => {
    if (ready) return
    incomingBaselined = false
    seenIncoming.clear()
  },
)

watch(
  () => friends.incoming.map((row) => row.username).join('\n'),
  (packed) => {
    if (!friends.ready || friends.loadError) return
    const names = packed ? packed.split('\n') : []
    if (!incomingBaselined) {
      incomingBaselined = true
      seenIncoming.clear()
      for (const name of names) seenIncoming.add(name.toLowerCase())
      if (names.length > 0) showFriendToast(friendRequestText(names), { go: openFriends, ms: 8000 })
      return
    }
    const fresh: string[] = []
    const next = new Set<string>()
    for (const name of names) {
      const id = name.toLowerCase()
      next.add(id)
      if (!seenIncoming.has(id)) fresh.push(name)
    }
    seenIncoming.clear()
    for (const id of next) seenIncoming.add(id)
    if (fresh.length === 0) return
    const text = friendRequestText(fresh)
    showFriendToast(text, { go: openFriends, ms: 8000 })
    notifyFriendRequest(text)
  },
)

watch(
  () =>
    friends.outgoing.map((row) => row.username).join('\n') +
    '\u0000' +
    friends.friends.map((row) => row.username).join('\n'),
  () => {
    if (!friends.ready || friends.loadError || !outgoingNoteKey()) return
    const outgoing = friends.outgoing.map((row) => row.username)
    const previous = readOutgoingNote()
    const fresh = friends.friends
      .map((row) => row.username)
      .filter((name) => previous.has(name.toLowerCase()))
    writeOutgoingNote(outgoing)
    if (fresh.length === 0) return
    const text = friendAcceptedText(fresh)
    showFriendToast(text, { go: openFriends, ms: 8000 })
    notifyFriendRequest(text)
  },
)

function onAcceptInvite(): void {
  const current = chatUi.invite
  if (!current) return
  chatUi.clearInvite()
  DuelSfx.prime()
  armDuelLandscape()
  void router.push({
    path: '/online-duel',
    query: { mode: 'join', code: current.code, username: player.username },
  })
}

const tournamentCall = ref<TournamentCallPayload | null>(null)
const seenTournamentCalls = new Set<string>()

function showTournamentCall(payload: TournamentCallPayload): void {
  if (seenTournamentCalls.has(payload.matchId)) return
  seenTournamentCalls.add(payload.matchId)
  const onThisMatch =
    route.name === 'online-duel' &&
    String(route.query.mode || '') === 'tournament' &&
    String(route.query.match || '') === payload.matchId
  if (!onThisMatch) tournamentCall.value = payload
  armTournamentPush()
  if (tournamentPushOn()) return
  if (!tournamentNoticesAllowed()) return
  if (document.visibilityState === 'visible') return
  try {
    const note = new Notification(t('tournament.yourMatch'), {
      body: t('tournament.callBody', {
        name: payload.opponent,
        minutes: TOURNAMENT_READY_MS / 60000,
      }),
    })
    note.onclick = () => {
      window.focus()
      note.close()
      tournamentCall.value = null
      void openTournamentMatch(payload.matchId, 'notice')
    }
  } catch {
    /* the in-page dialog still covers a visible tab */
  }
}

function onTournamentPlay(): void {
  void askTournamentPush()
  const current = tournamentCall.value
  tournamentCall.value = null
  if (!current) return
  void openTournamentMatch(current.matchId, 'dialog')
}

const tournament = useTournamentStore()
const tournamentOpen = ref<{ tournamentId: string; startsAt: string } | null>(null)
const OPEN_SEEN_KEY = 'dc-tournament-open-seen'
const seenTournamentOpens = new Set<string>()

function openSeen(id: string): boolean {
  if (seenTournamentOpens.has(id)) return true
  try {
    return sessionStorage.getItem(OPEN_SEEN_KEY) === id
  } catch {
    return false
  }
}

function rememberOpen(id: string): void {
  seenTournamentOpens.add(id)
  try {
    sessionStorage.setItem(OPEN_SEEN_KEY, id)
  } catch {
    /* private mode keeps the in-memory set */
  }
}

function showTournamentOpen(): void {
  const row = tournament.current
  const tournamentId = row?.tournamentId
  if (!row || row.sample || row.status !== 'registration' || !tournamentId) return
  if (tournament.joined) {
    rememberOpen(tournamentId)
    if (tournamentOpen.value?.tournamentId === tournamentId) tournamentOpen.value = null
    return
  }
  if (openSeen(tournamentId) || showIntro.value) return
  const inDuel = route.name === 'online-duel' && document.visibilityState === 'visible'
  if (inDuel) return
  rememberOpen(tournamentId)
  const startsAt = row.startsAt
  tournamentOpen.value = { tournamentId, startsAt }
  armTournamentPush()
  if (tournamentPushOn()) return
  if (!tournamentNoticesAllowed()) return
  if (document.visibilityState === 'visible') return
  try {
    const minutes = Math.max(1, Math.round((Date.parse(startsAt) - Date.now()) / 60000))
    const note = new Notification(t('tournament.openTitle'), {
      body: t('tournament.openBody', { minutes }),
    })
    note.onclick = () => {
      window.focus()
      note.close()
      tournamentOpen.value = { tournamentId, startsAt }
    }
  } catch {
    /* the in-page dialog still covers a visible tab */
  }
}

async function onTournamentOpenJoin(): Promise<void> {
  void askTournamentPush()
  tournamentOpen.value = null
  const name = String(route.name || '')
  if (name !== 'home' && name !== 'lobby' && name !== 'tournaments') {
    await router.push({ name: 'home' })
  }
  tournament.openPicker()
}

watch(
  () => [tournament.current, showIntro.value, route.name] as const,
  () => {
    showTournamentOpen()
  },
)

watch(
  () => auth.token,
  (token) => {
    if (token) armTournamentPush()
  },
)
const rankedLobby = useRankedLobbyStore()

const isPlayDuel = computed(() => PLAY_DUEL_ROUTES.has(String(route.name)))
const isAuthScreen = computed(() => AUTH_ROUTES.has(String(route.name)))
const showBanlistReward = computed(() => {
  if (!auth.token || showIntro.value || collection.welcomePending) return false
  if (isAuthScreen.value) return false
  if (banlistReward.dialogOpen) return true
  if (banlistReward.snoozed || banlistReward.available < 1) return false
  if (isPlayDuel.value || needDeck.value) return false
  return true
})
const onAccount = computed(
  () =>
    route.name === 'profile' ||
    route.name === 'settings' ||
    route.name === 'friends',
)
const hideDeckEditorFloats = computed(
  () => route.name === 'deck-editor' && menusCompact.value,
)
const showChat = computed(
  () =>
    auth.token &&
    !(route.name === 'recycle' && menusCompact.value) &&
    !hideDeckEditorFloats.value,
)
const showBugReport = computed(
  () =>
    !showIntro.value &&
    !isAuthScreen.value &&
    !isPlayDuel.value &&
    !(route.name === 'settings' && route.hash === '#feedback') &&
    route.name !== 'thanks' &&
    route.name !== 'recycle' &&
    !hideDeckEditorFloats.value,
)
const hideAppNav = computed(
  () =>
    showIntro.value ||
    isAuthScreen.value ||
    route.name === 'thanks' ||
    (isPlayDuel.value && (duelCompact.value || needsRotate.value)) ||
    (isPlayDuel.value && desktopFullscreen.value),
)
const showRotateOverlay = computed(
  () => isPlayDuel.value && needsRotate.value,
)
const chatCompact = computed(() => menusCompact.value)
const site = useSiteStore()
const showMaintenance = computed(() => {
  if (route.name === 'ops') return false
  const level = site.maintenance
  if (level === 'off') return false
  if (level === 'full') return true
  return !(route.name === 'online-duel' && route.query.mode !== 'local')
})
let sitePoll: number | null = null
let tournamentPoll: number | null = null
const shellClass = computed(() => ({
  'scheme-light': theme.colorScheme === 'light',
  'scheme-dark': theme.colorScheme === 'dark',
  'menus-compact': menusCompact.value,
  'menus-short': menusShort.value,
  'nav-hidden': hideAppNav.value,
}))

const myClientId = getClientId()
const chatMessages = ref<GlobalChatMessagePayload[]>([])
const onlineCount = ref<number | null>(null)
const onlineUsers = ref<string[]>([])
let chatClient: GlobalChatClient | null = null

const sendChat = (text: string) => {
  chatClient?.send(text)
}

const syncNeedDeck = () => {
  needDeck.value = route.query.needDeck === '1'
}

const closeNeedDeck = () => {
  needDeck.value = false
  if (route.query.needDeck) {
    const query = { ...route.query }
    delete query.needDeck
    void router.replace({ path: route.path, query })
  }
}

function onIntroDone(): void {
  if (!introForced()) sessionStorage.setItem(INTRO_KEY, '1')
  showIntro.value = false
}

/**
 * After first paint, store catalog JPEGs in Cache Storage (Login and Home).
 */
function scheduleCardArtWarmup(): void {
  const start = (): void => {
    void warmCatalogCardArt()
  }
  const idle = window as Window & {
    requestIdleCallback?: (
      cb: () => void,
      opts?: { timeout: number },
    ) => number
  }
  if (typeof idle.requestIdleCallback === 'function') {
    idle.requestIdleCallback(start, { timeout: 2500 })
    return
  }
  window.setTimeout(start, 400)
}

function onDocPointer(event: PointerEvent): void {
  const root = accountMenuEl.value
  if (!root || !accountMenuOpen.value) return
  if (!root.contains(event.target as Node)) accountMenuOpen.value = false
}

const onWelcomeContinue = () => {
  void collection.dismissWelcome()
  void router.push('/packs')
}

function bootChat(): void {
  chatClient?.dispose()
  chatClient = null
  chatMessages.value = []
  onlineCount.value = null
  onlineUsers.value = []
  chatUi.clearNotices()
  rankedLobby.setWaiting(null)
  const mutes = useChatMuteStore()
  if (!auth.token) {
    mutes.clear()
    friends.clear()
    chatUi.clearDirect()
    return
  }
  mutes.clear()
  void mutes.load(auth.token)
  void friends.load()
  chatClient = new GlobalChatClient({
    username: player.username,
    clientId: myClientId,
    avatar: player.avatar,
    handlers: {
      onMessage: (message) => {
        if (chatMessages.value.some((line) => line.id === message.id)) return
        chatMessages.value = [...chatMessages.value, message]
        chatUi.bumpGlobalUnread(
          message.clientId === myClientId,
          message.username,
        )
      },
      onHistory: (messages) => {
        const seen = new Set(chatMessages.value.map((line) => line.id))
        const hadLines = chatMessages.value.length > 0
        chatMessages.value = messages
        if (!hadLines) return
        for (const message of messages) {
          if (seen.has(message.id)) continue
          chatUi.bumpGlobalUnread(
            message.clientId === myClientId,
            message.username,
          )
        }
      },
      onOnline: (count, users) => {
        onlineCount.value = count
        onlineUsers.value = users
      },
      onQueue: (count, closed, casual, event) => {
        rankedLobby.setWaiting(count, closed, casual, event)
      },
      onReward: (payload) => {
        applyStaffReward(payload)
      },
      onMail: (payload) => {
        receivePlayerMail(payload)
      },
      onTournament: (payload) => {
        useTournamentStore().apply(payload)
      },
      onTournamentCall: (payload) => {
        showTournamentCall(payload)
      },
      onFriendUpdated: () => {
        void friends.load()
      },
      onDm: (payload) => {
        chatUi.ingestDm(payload)
      },
      onDmError: (payload) => {
        const name = payload.username?.trim() ?? ''
        chatUi.pushNotice(
          'global',
          name && payload.code === 'not_friends'
            ? t('chat.dm.notFriends', { name })
            : t('chat.dm.failed'),
        )
        if (payload.code === 'not_friends' && name) chatUi.closeDm(name, { dropUnread: true })
      },
      onInvite: (payload) => {
        chatUi.pushInvite(payload)
      },
      onInviteDeclined: (payload) => {
        showFriendToast(t('friends.inviteDeclined', { name: payload.username }))
      },
    },
  })
  chatUi.bindSite({
    sendDm: (username, text) => chatClient?.sendDm(username, text),
    declineInvite: (username) => chatClient?.declineInvite(username),
  })
}

function onLogout(): void {
  mobileMenuOpen.value = false
  accountMenuOpen.value = false
  auth.logout()
  resetAccountLocal()
  void router.push({ name: 'login' })
}

function syncNavFit(): void {
  if (menusCompact.value) {
    navCrowded.value = false
    return
  }
  if (hideAppNav.value) return
  const bar = navBarEl.value
  if (!bar || bar.clientWidth === 0) return
  const brand = bar.querySelector<HTMLElement>('.brand')
  const links = bar.querySelector<HTMLElement>('.nav-links')
  const extras = bar.querySelector<HTMLElement>('.nav-extras')
  const persistent = bar.querySelector<HTMLElement>('.nav-persistent')
  if (!brand || !links || !extras) return
  // brand–links, bar gap, extras–persistent. Same gaps as the expanded bar.
  const needed =
    brand.offsetWidth +
    links.scrollWidth +
    extras.offsetWidth +
    (persistent?.offsetWidth ?? 0) +
    28
  const slack = navCrowded.value ? 24 : 0
  navCrowded.value = bar.clientWidth < needed + slack
}

onMounted(() => {
  void site.refresh()
  sitePoll = window.setInterval(() => void site.refresh(), 3000)
  startAccountSync()
  syncNeedDeck()
  installUiSfx()
  window.addEventListener('keydown', onEscape)
  window.addEventListener('pointerdown', onDocPointer)
  bootChat()
  if (auth.token) {
    void hydrateAccount()
    void useTournamentStore().refresh()
    armTournamentPush()
  }
  tournamentPoll = window.setInterval(() => {
    if (auth.token && document.visibilityState === 'visible') {
      void useTournamentStore().refresh()
    }
  }, 20_000)
  scheduleCardArtWarmup()
  const bar = navBarEl.value
  if (bar) {
    navFitObserver = new ResizeObserver(() => syncNavFit())
    navFitObserver.observe(bar)
  }
  void document.fonts?.ready.then(() => syncNavFit())
  syncNavFit()
})

onUnmounted(() => {
  if (sitePoll != null) window.clearInterval(sitePoll)
  if (tournamentPoll != null) window.clearInterval(tournamentPoll)
  navFitObserver?.disconnect()
  navFitObserver = null
  window.removeEventListener('keydown', onEscape)
  window.removeEventListener('pointerdown', onDocPointer)
  chatClient?.dispose()
  chatClient = null
  Bgm.setScene('none')
})

watch(() => auth.token, (token) => {
  bootChat()
  if (token) {
    void hydrateAccount(true)
    void useTournamentStore().refresh()
  } else {
    resetAccountLocal()
    useTournamentStore().apply(null)
  }
})

watch(
  () => player.username,
  (name) => {
    chatClient?.setUsername(name)
    void nextTick(() => syncNavFit())
  },
)

watch(accountRename, (swap) => {
  if (!swap) return
  chatMessages.value = chatMessages.value.map((line) =>
    applyChatRename(line, swap.previous, swap.username),
  )
  onlineUsers.value = onlineUsers.value.map((name) =>
    chatNamesMatch(name, swap.previous) ? swap.username : name,
  )
})

watch(
  () => player.avatar,
  (look) => {
    chatClient?.setAvatar(look)
  },
  { deep: true },
)

watch(locale, (next) => {
  document.documentElement.lang = next
  void nextTick(() => syncNavFit())
}, { immediate: true })

watch(() => theme.colorScheme, (scheme) => {
  document.documentElement.style.colorScheme = scheme
  document.documentElement.style.backgroundColor =
    scheme === 'light' ? '#FFFBFE' : '#131316'
}, { immediate: true })
watch(() => route.query.needDeck, syncNeedDeck)
watch(anyPlayableDeck, (ready) => {
  if (ready && needDeck.value) closeNeedDeck()
})
watch(() => route.fullPath, () => {
  mobileMenuOpen.value = false
  accountMenuOpen.value = false
})
const changelogCanPrompt = computed(
  () =>
    Boolean(auth.token) &&
    !showIntro.value &&
    !collection.welcomePending &&
    !needDeck.value &&
    !menusCompact.value &&
    !isPlayDuel.value &&
    !isAuthScreen.value &&
    route.name !== 'changelog' &&
    route.name !== 'ops' &&
    !showMailDialog.value &&
    changelogNotice.offerDialog.value,
)
const showChangelogDialog = computed(
  () =>
    changelogDialogOpen.value &&
    Boolean(auth.token) &&
    !showIntro.value &&
    !collection.welcomePending &&
    !needDeck.value &&
    !menusCompact.value &&
    !isPlayDuel.value &&
    !isAuthScreen.value &&
    route.name !== 'changelog' &&
    route.name !== 'ops' &&
    !showMailDialog.value,
)

watch(changelogCanPrompt, (show) => {
  if (!show || changelogDialogOpen.value) return
  dialogSnapshot.value = [...changelogNotice.unseen.value]
  changelogDialogOpen.value = true
  changelogNotice.markDialogShown()
}, { immediate: true })

const eventQueueCanPrompt = computed(
  () =>
    Boolean(auth.token) &&
    route.name === 'home' &&
    !showIntro.value &&
    !collection.welcomePending &&
    !needDeck.value &&
    !isPlayDuel.value &&
    !isAuthScreen.value &&
    !changelogCanPrompt.value &&
    !showChangelogDialog.value &&
    !eventQueueHeld.value &&
    eventQueueNotice.offerDialog.value,
)
const showEventQueueDialog = computed(
  () =>
    eventQueueDialogOpen.value &&
    eventQueueNotice.offerDialog.value &&
    route.name === 'home' &&
    Boolean(auth.token) &&
    !showIntro.value &&
    !isPlayDuel.value &&
    !isAuthScreen.value,
)

watch(eventQueueCanPrompt, (show) => {
  if (!eventQueueNotice.offerDialog.value) {
    eventQueueDialogOpen.value = false
    return
  }
  if (!show || eventQueueDialogOpen.value) return
  eventQueueDialogOpen.value = true
}, { immediate: true })

const lastCallCanPrompt = computed(
  () =>
    Boolean(auth.token) &&
    route.name === 'home' &&
    !showIntro.value &&
    !collection.welcomePending &&
    !needDeck.value &&
    !isPlayDuel.value &&
    !isAuthScreen.value &&
    !changelogCanPrompt.value &&
    !showChangelogDialog.value &&
    !eventQueueCanPrompt.value &&
    !showEventQueueDialog.value &&
    !lastCallHeld.value &&
    lastCallNotice.offerDialog.value,
)
const showLastCallDialog = computed(
  () =>
    lastCallDialogOpen.value &&
    !lastCallNotice.finished.value &&
    route.name === 'home' &&
    Boolean(auth.token) &&
    !showIntro.value &&
    !isPlayDuel.value &&
    !isAuthScreen.value,
)

watch(lastCallCanPrompt, (show) => {
  if (lastCallNotice.finished.value) {
    lastCallDialogOpen.value = false
    return
  }
  if (!show || lastCallDialogOpen.value) return
  lastCallDialogOpen.value = true
}, { immediate: true })

function onChangelogSnooze(): void {
  changelogNotice.snoozeToday()
  changelogDialogOpen.value = false
}

function onLastCallClose(): void {
  lastCallDialogOpen.value = false
  lastCallHeld.value = true
}

function onLastCallSnooze(): void {
  lastCallNotice.snoozeToday()
  lastCallDialogOpen.value = false
}

function onEventQueueClose(): void {
  eventQueueDialogOpen.value = false
  eventQueueHeld.value = true
}

function onEventQueueSnooze(): void {
  eventQueueNotice.snoozeToday()
  eventQueueDialogOpen.value = false
}

function onEventQueuePlay(): void {
  eventQueueDialogOpen.value = false
  eventQueueHeld.value = true
  startEvent()
}

const pushAskHidden = ref(pushAskDismissed())
const showPushAsk = computed(() => {
  if (!auth.token || pushAskHidden.value) return false
  if (showIntro.value || isPlayDuel.value || isAuthScreen.value) return false
  if (collection.welcomePending || needDeck.value) return false
  if (showChangelogDialog.value || showEventQueueDialog.value || showLastCallDialog.value || showMailDialog.value) return false
  if (showBanlistReward.value) return false
  if (tournamentOpen.value || tournamentCall.value) return false
  if (typeof Notification === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false
  }
  return Notification.permission === 'default'
})

function onPushAskEnable(): void {
  void askTournamentPush()
  dismissPushAsk()
  pushAskHidden.value = true
}

function onPushAskLater(): void {
  dismissPushAsk()
  pushAskHidden.value = true
}

watch(
  () => route.name,
  (name, prev) => {
    Bgm.setScene(bgmSceneForRoute(name))
    if (name === 'changelog') changelogDialogOpen.value = false
    if (name === 'home' && prev != null && prev !== 'home') {
      lastCallHeld.value = false
      eventQueueHeld.value = false
    }
  },
  { immediate: true },
)
watch(
  isPlayDuel,
  (on) => {
    if (on) armDuelLandscape()
    else releaseDuelOrientation()
  },
  { immediate: true },
)
watch(navCompact, (on) => {
  if (!on) mobileMenuOpen.value = false
})
watch(navCrowded, (on) => {
  if (on) accountMenuOpen.value = false
})
watch(
  [displayCoins, () => progress.level, mailBadge, menusCompact, hideAppNav],
  () => {
    void nextTick(() => syncNavFit())
  },
)
watch(chatCompact, (on) => {
  if (on) chatUi.close()
})

function openDuelPick(): void {
  mobileMenuOpen.value = false
  accountMenuOpen.value = false
  duelPickOpen.value = true
}

function startVsAi(): void {
  duelPickOpen.value = false
  DuelSfx.prime()
  armDuelLandscape()
  void router.push({ path: '/online-duel', query: { mode: 'local' } })
}

function startRanked(): void {
  if (rankedLobby.closed) return
  duelPickOpen.value = false
  DuelSfx.prime()
  armDuelLandscape()
  void router.push({
    path: '/online-duel',
    query: { mode: 'matchmaking', username: player.username },
  })
}

function startCasual(): void {
  if (rankedLobby.closed) return
  duelPickOpen.value = false
  DuelSfx.prime()
  armDuelLandscape()
  void router.push({
    path: '/online-duel',
    query: { mode: 'casual', username: player.username },
  })
}

function startEvent(): void {
  if (!site.eventQueue) return
  duelPickOpen.value = false
  DuelSfx.prime()
  armDuelLandscape()
  void router.push({
    path: '/online-duel',
    query: { mode: 'event', username: player.username },
  })
}

function onEscape(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return
  if (changelogDialogOpen.value) {
    changelogDialogOpen.value = false
    return
  }
  if (eventQueueDialogOpen.value) {
    eventQueueDialogOpen.value = false
    eventQueueHeld.value = true
    return
  }
  if (duelPickOpen.value) {
    duelPickOpen.value = false
    return
  }
  if (showIntro.value) {
    onIntroDone()
    return
  }
  accountMenuOpen.value = false
  mobileMenuOpen.value = false
}
</script>

<style>
.app-shell {
  --md-sys-color-surface: #131316;
  --md-sys-color-on-surface: #e5e1e6;
  --md-sys-color-on-surface-variant: #c6c5d0;
  --md-sys-color-surface-container: #1f1f23;
  --md-sys-color-surface-container-high: #2a2a2e;
  --md-sys-color-outline: #90909a;
  --md-sys-color-outline-variant: #46464f;
  --md-sys-color-primary: #e8c547;
  --md-sys-color-on-primary: #1c1b1f;
  --md-sys-color-primary-container: #6e5c1a;
  --md-sys-color-on-primary-container: #f5f5f5;
  --md-scrim: rgba(4, 6, 16, 0.82);
  --md-state-layer: rgba(229, 225, 230, 0.08);
  --md-elevation-1:
    0 1px 2px rgba(0, 0, 0, 0.3), 0 1px 3px 1px rgba(0, 0, 0, 0.15);
  --md-elevation-2:
    0 1px 2px rgba(0, 0, 0, 0.3), 0 2px 6px 2px rgba(0, 0, 0, 0.15);
  --md-motion-standard: cubic-bezier(0.2, 0, 0, 1);
  --md-motion-emphasized: cubic-bezier(0.05, 0.7, 0.1, 1);
  --md-motion-short: 200ms;
  --md-motion-medium: 300ms;
  --md-motion-long: 500ms;
  --app-bar-height: 64px;
  --app-nav-offset: calc(var(--app-bar-height) + env(safe-area-inset-top, 0px));
  min-height: 100vh;
  min-height: 100dvh;
  background: var(--md-sys-color-surface);
  color: var(--md-sys-color-on-surface);
  font-family: Roboto, system-ui, sans-serif;
}

.app-shell.menus-short {
  --app-bar-height: 48px;
}

.app-shell.nav-hidden {
  --app-nav-offset: 0px;
}

.scheme-light {
  color-scheme: light;
}

.scheme-dark {
  color-scheme: dark;
}

.top-app-bar {
  position: relative;
  z-index: 40;
  height: var(--app-nav-offset);
  padding-top: env(safe-area-inset-top, 0px);
  background: var(--md-sys-color-surface-container);
  border-bottom: 1px solid var(--md-sys-color-outline-variant);
  /* Off-screen link measurement must not widen the page. */
  contain: layout;
}

.bar-wrap {
  width: 100%;
  margin: 0;
  padding: 0 max(16px, env(safe-area-inset-right, 0px))
    0 max(16px, env(safe-area-inset-left, 0px));
}

.bar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  height: var(--app-bar-height);
}

.nav-start {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
}

.nav-links {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
}

.nav-extras,
.nav-persistent {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.nav-measure {
  position: absolute;
  width: max-content;
  visibility: hidden;
  pointer-events: none;
}

.nav-end {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex: 0 0 auto;
}

.app-main {
  padding-left: env(safe-area-inset-left, 0px);
  padding-right: env(safe-area-inset-right, 0px);
}

.app-main.has-bug-report {
  padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
}

.brand {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  flex-shrink: 0;
}

.brand-mark {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  object-fit: cover;
  flex-shrink: 0;
}

.app-shell.menus-short .brand-mark {
  width: 30px;
  height: 30px;
}

button.nav-link {
  border: 0;
  cursor: pointer;
  font-family: inherit;
}

button.nav-link.nav-duel,
button.nav-link.nav-duel:hover,
button.nav-link.nav-duel.active {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.nav-link {
  color: var(--md-sys-color-on-surface-variant);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 200ms cubic-bezier(0.2, 0, 0, 1);
}

.nav-profile {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-left: 8px;
  flex-shrink: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  font: inherit;
}

.caret {
  width: 16px;
  height: 16px;
  opacity: 0.8;
}

.account-menu {
  position: relative;
}

.account-pop {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  z-index: 50;
  min-width: 200px;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid var(--md-sys-color-outline-variant);
  background: var(--md-sys-color-surface-container-high);
  box-shadow: var(--md-elevation-1);
}

.account-item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  text-align: left;
  cursor: pointer;
}

.account-item:hover {
  background: var(--md-state-layer);
}

.account-item.router-link-active {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.account-sep {
  height: 1px;
  margin: 6px 8px;
  background: var(--md-sys-color-outline-variant);
}

.nav-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px 5px 6px;
  border-radius: 999px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-decoration: none;
  box-shadow: var(--md-elevation-1);
  border: 1px solid var(--md-sys-color-outline-variant);
}

.coin-chip {
  color: var(--md-sys-color-primary);
}

.coin-amt {
  min-width: 1.5ch;
  font-variant-numeric: tabular-nums;
}

.level-chip {
  color: var(--md-sys-color-on-surface);
}

.coin-chip.is-crediting {
  animation: coin-credit var(--md-motion-medium) var(--md-motion-emphasized);
}

@keyframes coin-credit {
  0% { transform: scale(1); }
  35% { transform: scale(1.12); box-shadow: 0 0 0 4px color-mix(in srgb, var(--md-sys-color-primary) 35%, transparent); }
  100% { transform: scale(1); }
}

@media (prefers-reduced-motion: reduce) {
  .coin-chip.is-crediting { animation: none; }
}

.drawer-stats {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 4px 16px 8px;
}

.nav-link:hover {
  background: var(--md-state-layer);
  color: var(--md-sys-color-on-surface);
}

.nav-link.active {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.nav-missions {
  position: relative;
}

.nav-news {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.mobile-nav-link.mobile-nav-news {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-pending {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--md-sys-color-primary);
  flex-shrink: 0;
}

.nav-pending-menu {
  position: absolute;
  top: 8px;
  left: 8px;
}

.friend-toast {
  position: fixed;
  left: 50%;
  bottom: calc(96px + env(safe-area-inset-bottom, 0px));
  z-index: 80;
  max-width: min(420px, calc(100vw - 32px));
  margin: 0;
  padding: 12px 16px;
  border: 0;
  border-radius: 16px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 14px;
  text-align: center;
  cursor: pointer;
  transform: translateX(-50%);
  box-shadow: var(--md-elevation-2);
}

.nav-notice {
  position: absolute;
  top: 0;
  right: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  pointer-events: none;
}

.nav-notice-inline {
  position: static;
  margin-left: 8px;
}

.nav-notice-menu {
  top: 6px;
  right: 6px;
}

.nav-notice-menu.nav-notice-alt {
  top: auto;
  bottom: 4px;
}

.mobile-nav-link {
  display: block;
  color: var(--md-sys-color-on-surface-variant);
  padding: 12px 16px;
  border-radius: 28px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  min-height: 44px;
}

.mobile-nav-link.mobile-nav-missions {
  display: flex;
  align-items: center;
}

button.mobile-nav-duel,
button.mobile-nav-duel:hover,
button.mobile-nav-duel.active {
  border: 0;
  width: 100%;
  cursor: pointer;
  font-family: inherit;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.mobile-nav-duel {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 8px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  box-shadow: var(--md-elevation-1);
}

.mobile-nav-duel:hover,
.mobile-nav-duel.router-link-active,
.mobile-nav-duel.active {
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  filter: brightness(1.06);
}

.mobile-profile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 8px;
  padding-bottom: 8px;
  color: var(--md-sys-color-on-surface);
  font-size: 16px;
}

.mobile-profile-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-shell.menus-short .mobile-profile {
  gap: 10px;
  padding-top: 4px;
  padding-bottom: 4px;
  font-size: 14px;
}

.mobile-nav-link:hover,
.mobile-nav-link.router-link-active,
.mobile-nav-link.active {
  background: var(--md-state-layer);
}

.mobile-nav-link.active {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.icon-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  border: 0;
  border-radius: 24px;
  background: transparent;
  color: var(--md-sys-color-on-surface-variant);
  cursor: pointer;
}

.app-shell.menus-short .icon-btn {
  width: 40px;
  height: 40px;
}

.icon-btn:hover {
  background: var(--md-state-layer);
}

.icon-btn.icon-active,
a.nav-mail.router-link-active {
  color: var(--md-sys-color-primary);
}

a.nav-mail {
  text-decoration: none;
}

.mobile-nav-mail {
  display: flex;
  align-items: center;
  gap: 8px;
}

.drawer-scrim {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.45);
}

.mobile-drawer {
  display: flex;
  flex-direction: column;
  width: min(320px, 86vw);
  height: 100%;
  max-height: 100%;
  overflow-y: auto;
  padding: 8px max(16px, env(safe-area-inset-right, 0px))
    max(16px, env(safe-area-inset-bottom, 0px)) 16px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
}

.app-shell.menus-short .mobile-drawer {
  width: min(280px, 70vw);
}

.drawer-close {
  align-self: flex-end;
  margin: 4px 0 8px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
}

.drawer-extras {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--md-sys-color-outline-variant);
}

.theme-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 44px;
  padding: 4px 8px 4px 8px;
}

.theme-toggle-copy {
  flex: 1 1 auto;
  color: var(--md-sys-color-on-surface);
  font-size: 14px;
  font-weight: 500;
}

.theme-toggle-mode {
  min-width: 4.5em;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 12px;
  font-weight: 500;
}

.theme-switch {
  position: relative;
  flex: 0 0 auto;
  width: 52px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 16px;
  background: var(--md-sys-color-surface-container-high);
  box-shadow: inset 0 0 0 1px var(--md-sys-color-outline-variant);
  cursor: pointer;
}

.theme-switch[aria-checked='true'] {
  background: var(--md-sys-color-primary-container);
  box-shadow: inset 0 0 0 1px var(--md-sys-color-primary);
}

.theme-switch-knob {
  position: absolute;
  top: 4px;
  left: 4px;
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background: var(--md-sys-color-surface);
  color: var(--md-sys-color-on-surface-variant);
  box-shadow: var(--md-elevation-1);
  transition: transform 180ms cubic-bezier(0.2, 0, 0, 1);
}

.theme-switch-knob.light {
  transform: translateX(20px);
  color: var(--md-sys-color-on-primary-container);
}

.theme-switch-knob svg {
  width: 16px;
  height: 16px;
}

.drawer-btn {
  border: 0;
  background: transparent;
  width: 100%;
  text-align: left;
  cursor: pointer;
}

.drawer-logout {
  margin-top: 4px;
  color: var(--md-sys-color-on-surface-variant);
}

.drawer-support {
  display: flex;
  justify-content: center;
  margin-top: auto;
  padding-top: 16px;
}

.drawer-lang {
  margin-left: auto;
}

.app-shell.menus-short .drawer-extras {
  flex-direction: column;
  align-items: stretch;
}

.app-shell.menus-short .drawer-lang {
  margin-left: 0;
  align-self: flex-start;
}
</style>
